import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Users as UsersIcon, Edit, Save, X, Check, Eye, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import pb from '../lib/pocketbaseClient';

// Allowed team roles (same as Users page)
const ALLOWED_ROLES = ['admin', 'super_admin', 'manager', 'sales', 'designer', 'hr', 'creator'];

// Apps that we can assign permissions to
const APPS = [
  { id: 'web', label: 'Web App' },
  { id: 'admin', label: 'Admin Panel' },
  { id: 'office', label: 'Office CRM' },
  { id: 'hr', label: 'HR Panel' },
  { id: 'files', label: 'Files Manager' },
  { id: 'accounts', label: 'Accounts' },
];

// Default permissions structure
const defaultPermissions = () => {
  const perms = {};
  APPS.forEach(app => {
    perms[app.id] = { read: false, write: false, delete: false };
  });
  return perms;
};

const Roles = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // ─── Fetch team members ──────────────────────────────────
  const fetchUsers = async () => {
    try {
      const records = await pb.collection('users').getFullList({
        sort: '-created',
      });
      const team = records.filter((user) => ALLOWED_ROLES.includes(user.role));
      setUsers(team);
    } catch (err) {
      console.error('Failed to fetch users', err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ─── Open permission dialog ─────────────────────────────
  const openPermissionDialog = (user) => {
    setEditingUser(user);
    // Load existing permissions or default
    const userPerms = user.permissions || defaultPermissions();
    setPermissions(userPerms);
    setIsDialogOpen(true);
  };

  // ─── Toggle permission for an app ────────────────────────
  const togglePermission = (appId, permType) => {
    setPermissions((prev) => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        [permType]: !prev[appId][permType],
      },
    }));
  };

  // ─── Toggle all permissions for an app ──────────────────
  const toggleAllForApp = (appId, value) => {
    setPermissions((prev) => ({
      ...prev,
      [appId]: {
        read: value,
        write: value,
        delete: value,
      },
    }));
  };

  // ─── Save permissions ──────────────────────────────────
  const savePermissions = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await pb.collection('users').update(editingUser.id, {
        permissions: permissions,
      });
      toast.success('Permissions updated successfully');
      // Update local user list
      setUsers(users.map(u => u.id === editingUser.id ? { ...u, permissions } : u));
      setIsDialogOpen(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Save permissions error:', err);
      toast.error(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  // ─── Role badge ─────────────────────────────────────────
  const RoleBadge = ({ role }) => {
    const colors = {
      admin: 'bg-red-100 text-red-700 border-red-200',
      super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      sales: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      designer: 'bg-amber-100 text-amber-700 border-amber-200',
      hr: 'bg-pink-100 text-pink-700 border-pink-200',
      creator: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    return <Badge className={`${colors[role] || 'bg-gray-100 text-gray-700'} border`}>{role}</Badge>;
  };

  // ─── Permission display ─────────────────────────────────
  const PermissionSummary = ({ perms }) => {
    const appsWithAccess = APPS.filter(app => perms[app.id] && (perms[app.id].read || perms[app.id].write || perms[app.id].delete));
    if (appsWithAccess.length === 0) return <span className="text-slate-400 text-sm">No access</span>;
    return (
      <span className="text-xs text-slate-600">
        {appsWithAccess.map(app => app.label).join(', ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative">
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <UsersIcon className="w-8 h-8" /> Roles & Permissions
          </h1>
          <p className="text-white/80">Assign app access and permissions to team members</p>
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-slate-600 font-semibold">Name</TableHead>
                <TableHead className="text-slate-600 font-semibold">Email</TableHead>
                <TableHead className="text-slate-600 font-semibold">Role</TableHead>
                <TableHead className="text-slate-600 font-semibold">Current Access</TableHead>
                <TableHead className="text-slate-600 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">
                    No team members found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="hover:bg-indigo-50/50 transition">
                    <TableCell className="font-medium">{user.name || '—'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell><RoleBadge role={user.role} /></TableCell>
                    <TableCell>
                      <PermissionSummary perms={user.permissions || defaultPermissions()} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPermissionDialog(user)}
                        className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                        title="Edit Permissions"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ─── Edit Permissions Dialog ─────────────────────────── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-indigo-600 flex items-center gap-2">
              <Edit className="w-6 h-6" /> Permissions for {editingUser?.name || 'User'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <p className="text-sm text-slate-500">
              Grant access to apps and define what actions the user can perform.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {APPS.map((app) => {
                const appPerms = permissions[app.id] || { read: false, write: false, delete: false };
                const hasAny = appPerms.read || appPerms.write || appPerms.delete;
                return (
                  <Card key={app.id} className={`border ${hasAny ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center mb-2">
                        <Label className="font-semibold text-slate-700">{app.label}</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAllForApp(app.id, !hasAny)}
                          className="text-xs"
                        >
                          {hasAny ? 'Remove All' : 'Grant All'}
                        </Button>
                      </div>
                      <div className="flex gap-6">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={appPerms.read}
                            onCheckedChange={() => togglePermission(app.id, 'read')}
                          />
                          <span className="text-sm flex items-center gap-1"><Eye className="w-3 h-3" /> Read</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={appPerms.write}
                            onCheckedChange={() => togglePermission(app.id, 'write')}
                          />
                          <span className="text-sm flex items-center gap-1"><Pencil className="w-3 h-3" /> Write</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={appPerms.delete}
                            onCheckedChange={() => togglePermission(app.id, 'delete')}
                          />
                          <span className="text-sm flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button
              onClick={savePermissions}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Roles;